import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ShieldCheck, Map, ChevronRight, Bus, MapPin, Ticket } from 'lucide-react';

const cities = [
  { name: 'Tamale', x: 294, y: 83 },
  { name: 'Kumasi', x: 269, y: 253 },
  { name: 'Takoradi', x: 132, y: 431 },
  { name: 'Cape Coast', x: 228, y: 456 },
  { name: 'Accra', x: 371, y: 464 },
  { name: 'Ho', x: 421, y: 313 },
];

const routes = [
  { d: 'M371 464 C323 405 299 324 269 253', delay: 0 },
  { d: 'M269 253 C241 183 258 118 294 83', delay: 0.25 },
  { d: 'M269 253 C216 281 168 352 132 431', delay: 0.45 },
  { d: 'M269 253 C326 300 366 371 371 464', delay: 0.65 },
  { d: 'M371 464 C405 416 427 364 421 313', delay: 0.85 },
  { d: 'M132 431 C168 452 197 459 228 456 C286 450 335 457 371 464', delay: 1.05 },
];

function BusGlyph({ delay = 0, path }: { delay?: number; path: { x: number[]; y: number[] } }) {
  return (
    <motion.g
      animate={{ x: path.x, y: path.y, rotate: [0, 4, -3, 0] }}
      transition={{ duration: 8.5, repeat: Infinity, ease: 'easeInOut', delay }}
    >
      <rect x="-18" y="-11" width="36" height="22" rx="6" fill="#F5961E" />
      <rect x="-12" y="-7" width="8" height="7" rx="1.5" fill="#FFFFFF" opacity="0.9" />
      <rect x="-1" y="-7" width="8" height="7" rx="1.5" fill="#FFFFFF" opacity="0.9" />
      <rect x="10" y="-7" width="5" height="7" rx="1.5" fill="#093A7C" opacity="0.9" />
      <circle cx="-10" cy="11" r="3.5" fill="#093A7C" />
      <circle cx="10" cy="11" r="3.5" fill="#093A7C" />
    </motion.g>
  );
}

function GhanaRouteMap() {
  return (
    <motion.div
      className="absolute inset-x-0 bottom-[-11rem] z-0 w-full max-w-[23rem] mx-auto opacity-70 sm:max-w-[27rem] lg:relative lg:inset-auto lg:bottom-auto lg:z-auto lg:max-w-[34rem] lg:mx-0 lg:opacity-100"
      initial={{ opacity: 0, y: 24, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.8, ease: 'easeOut', delay: 0.15 }}
    >
      <div className="absolute -inset-x-8 bottom-0 h-20 bg-brand-blue/10 blur-2xl" />
      <svg
        viewBox="0 0 520 560"
        role="img"
        aria-label="Animated Ghana map with Wote InterCity bus routes"
        className="relative w-full drop-shadow-2xl"
      >
        <defs>
          <linearGradient id="ghanaMapFill" x1="0%" x2="100%" y1="0%" y2="100%">
            <stop offset="0%" stopColor="#F8FAFC" />
            <stop offset="100%" stopColor="#E2E8F0" />
          </linearGradient>
          <filter id="routeGlow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="4" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <motion.path
          d="M281 29 C244 38 223 63 225 94 C199 104 187 129 194 158 C177 181 184 207 199 229 C176 257 146 274 116 292 C82 313 71 351 91 382 C74 407 83 444 116 458 C143 470 174 473 199 461 C226 482 266 485 292 465 C326 488 365 483 383 451 C414 443 439 415 434 381 C463 359 462 324 434 302 C443 269 430 234 400 219 C407 186 388 158 356 151 C353 120 332 101 303 100 C305 70 299 43 281 29 Z"
          fill="url(#ghanaMapFill)"
          stroke="#093A7C"
          strokeWidth="5"
          strokeLinejoin="round"
          initial={{ pathLength: 0, opacity: 0.4 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={{ duration: 1.5, ease: 'easeInOut' }}
        />

        <path
          d="M281 29 C244 38 223 63 225 94 C199 104 187 129 194 158 C177 181 184 207 199 229 C176 257 146 274 116 292 C82 313 71 351 91 382 C74 407 83 444 116 458 C143 470 174 473 199 461 C226 482 266 485 292 465 C326 488 365 483 383 451 C414 443 439 415 434 381 C463 359 462 324 434 302 C443 269 430 234 400 219 C407 186 388 158 356 151 C353 120 332 101 303 100 C305 70 299 43 281 29 Z"
          fill="none"
          stroke="#FFFFFF"
          strokeWidth="2"
          strokeOpacity="0.7"
        />

        {routes.map((route) => (
          <g key={route.d}>
            <motion.path
              d={route.d}
              fill="none"
              stroke="#F5961E"
              strokeWidth="7"
              strokeLinecap="round"
              strokeDasharray="10 14"
              filter="url(#routeGlow)"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ pathLength: 1, opacity: 0.95 }}
              transition={{ duration: 1.4, ease: 'easeInOut', delay: route.delay }}
            />
            <motion.path
              d={route.d}
              fill="none"
              stroke="#093A7C"
              strokeWidth="2"
              strokeLinecap="round"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ pathLength: 1, opacity: 0.75 }}
              transition={{ duration: 1.4, ease: 'easeInOut', delay: route.delay + 0.15 }}
            />
          </g>
        ))}

        {cities.map((city, index) => (
          <motion.g
            key={city.name}
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.45, delay: 0.7 + index * 0.08 }}
          >
            <motion.circle
              cx={city.x}
              cy={city.y}
              r="15"
              fill="#F5961E"
              opacity="0.18"
              animate={{ scale: [1, 1.55, 1], opacity: [0.18, 0.05, 0.18] }}
              transition={{ duration: 2.4, repeat: Infinity, delay: index * 0.2 }}
            />
            <circle cx={city.x} cy={city.y} r="6" fill="#093A7C" stroke="#FFFFFF" strokeWidth="3" />
            <text
              x={city.x + 12}
              y={city.y - 10}
              fill="#093A7C"
              fontSize="17"
              fontWeight="800"
              paintOrder="stroke"
              stroke="#FFFFFF"
              strokeWidth="5"
            >
              {city.name}
            </text>
          </motion.g>
        ))}

        <BusGlyph delay={1.1} path={{ x: [371, 342, 306, 269], y: [464, 401, 318, 253] }} />
        <BusGlyph delay={3.2} path={{ x: [269, 244, 263, 294], y: [253, 184, 120, 83] }} />
        <BusGlyph delay={2.1} path={{ x: [132, 184, 251, 317, 371], y: [431, 452, 457, 459, 464] }} />
      </svg>
    </motion.div>
  );
}

export function Home() {
  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const stagger = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-brand-white min-h-[calc(100svh-8rem)] py-8 sm:py-10 lg:min-h-[calc(100svh-5rem)] lg:py-14">
        <div className="absolute inset-0 bg-[linear-gradient(120deg,rgba(9,58,124,0.06),rgba(245,150,30,0.08)_45%,rgba(255,255,255,0)_75%)] z-0" />
        <div className="absolute inset-x-0 top-0 h-px bg-brand-blue/10" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 grid min-h-[calc(100svh-13rem)] grid-cols-1 lg:min-h-[calc(100svh-10rem)] lg:grid-cols-[0.92fr_1.08fr] items-center gap-10 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="relative z-10 text-center lg:text-left"
          >
            <div className="inline-flex items-center gap-2 rounded-full bg-brand-blue/10 px-4 py-2 text-sm font-semibold text-brand-blue mb-6">
              <MapPin className="w-4 h-4 text-brand-orange" />
              Ghana intercity routes
            </div>
            <h1 className="text-5xl md:text-7xl font-extrabold text-brand-blue tracking-tight mb-7">
              Wote <span className="text-brand-orange">InterCity</span>
            </h1>
            <p className="mt-4 text-xl md:text-2xl text-secondary-700 max-w-2xl mx-auto lg:mx-0 mb-10 leading-relaxed">
              Book bus journeys across Ghana with clear routes, dependable schedules, and real-time trip updates from your phone.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Link
                to="/signup"
                className="bg-brand-orange text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-brand-orange/90 transition-all shadow-lg hover:shadow-xl hover:-translate-y-1 flex items-center justify-center gap-2"
              >
                Book Your Seat Now
                <ChevronRight className="w-5 h-5" />
              </Link>
              <a
                href="#features"
                className="bg-white text-brand-blue border-2 border-brand-blue/20 px-8 py-4 rounded-full font-bold text-lg hover:bg-brand-blue/5 transition-all flex items-center justify-center"
              >
                Learn More
              </a>
            </div>

            <div className="mt-10 hidden grid-cols-3 gap-4 text-left max-w-xl mx-auto sm:grid lg:mx-0">
              {[
                ['6', 'Route hubs'],
                ['24/7', 'Booking access'],
                ['Live', 'Trip tracking'],
              ].map(([value, label]) => (
                <motion.div
                  key={label}
                  className="border-l-2 border-brand-orange/70 pl-4"
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.35 }}
                >
                  <div className="text-2xl font-extrabold text-brand-blue">{value}</div>
                  <div className="text-sm font-medium text-secondary-500">{label}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <GhanaRouteMap />
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-brand-blue mb-4">Why Choose Wote InterCity?</h2>
            <p className="text-lg text-secondary-600 max-w-2xl mx-auto">
              We provide a seamless travel experience from booking to arrival.
            </p>
          </div>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
            variants={stagger}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {[
              {
                icon: <Ticket className="w-8 h-8 text-brand-orange" />,
                title: "Easy Booking",
                description: "Book tickets instantly via our app or web platform in just a few taps."
              },
              {
                icon: <Map className="w-8 h-8 text-brand-orange" />,
                title: "Extensive Routes",
                description: "Travel between Accra, Kumasi, Tamale, Takoradi, Cape Coast, Ho, and more."
              },
              {
                icon: <Bus className="w-8 h-8 text-brand-orange" />,
                title: "Tracked Buses",
                description: "See route progress and trip updates so your journey is easier to follow."
              },
              {
                icon: <ShieldCheck className="w-8 h-8 text-brand-orange" />,
                title: "Safe & Secure",
                description: "Your safety is our priority with maintained vehicles and verified drivers."
              }
            ].map((feature, idx) => (
              <motion.div 
                key={idx}
                variants={fadeIn}
                className="bg-brand-white p-7 rounded-lg shadow-sm border border-secondary-100 hover:-translate-y-1 hover:shadow-md transition-all"
              >
                <div className="w-14 h-14 bg-brand-orange/10 rounded-lg flex items-center justify-center mb-6">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-brand-blue mb-3">{feature.title}</h3>
                <p className="text-secondary-600 leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-brand-blue py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 -left-40 w-96 h-96 bg-brand-orange rounded-full blur-3xl mix-blend-multiply" />
          <div className="absolute bottom-0 -right-40 w-96 h-96 bg-brand-orange rounded-full blur-3xl mix-blend-multiply" />
        </div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to start your journey?
          </h2>
          <p className="text-xl text-blue-100 mb-10">
            Create an account today to manage your bookings, save preferences, and earn rewards.
          </p>
          <Link
            to="/signup"
            className="inline-block bg-brand-orange text-white px-10 py-4 rounded-full font-bold text-lg hover:bg-brand-orange/90 hover:scale-105 transition-all shadow-lg"
          >
            Create an Account
          </Link>
        </div>
      </section>
    </div>
  );
}
